import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, FilePlus2, FolderClosed, ShieldCheck, Settings, LogOut, Menu, X, Users, Inbox, FileCheck } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const FIRM_NAV = [
  { to: "/inicio", label: "Inicio", icon: Home },
  { to: "/nuevo-analisis", label: "Plantear un trabajo", icon: FilePlus2 },
  { to: "/asuntos", label: "Solicitudes y encargos", icon: FolderClosed },
  { to: "/seguridad", label: "Seguridad", icon: ShieldCheck },
  { to: "/cuenta", label: "Protección de datos", icon: FileCheck },
  { to: "/ajustes", label: "Ajustes", icon: Settings },
];

const ADMIN_NAV = [
  { to: "/admin/solicitudes", label: "Solicitudes", icon: Inbox },
  { to: "/admin/asuntos", label: "Asuntos", icon: FolderClosed },
  { to: "/admin/despachos", label: "Despachos", icon: Users },
  { to: "/seguridad", label: "Seguridad", icon: ShieldCheck },
];

export function AppLayout({ children, variant = "firm" }) {
  const nav = variant === "admin" ? ADMIN_NAV : FIRM_NAV;
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/acceso");
  };

  const isActive = (to) => loc.pathname === to || loc.pathname.startsWith(to + "/");

  const SideInner = (
    <>
      <div style={{ padding: "26px 22px 20px" }}>
        <Link to={variant === "admin" ? "/admin/asuntos" : "/inicio"} style={{ textDecoration: "none", display: "flex", alignItems: "baseline", gap: 10 }}>
          <Logo height={34} />
          {variant === "admin" && <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".12em", color: "var(--burgundy-med)", textTransform: "uppercase" }}>Operador</span>}
        </Link>
      </div>
      <nav style={{ display: "flex", flexDirection: "column", gap: 3, padding: "6px 12px" }}>
        {nav.map(({ to, label, icon: Icon }) => {
          const active = isActive(to);
          return (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s|á|í/g, (m) => ({ "á": "a", "í": "i", " ": "-" }[m]))}`}
              onClick={() => setOpen(false)}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "11px 14px",
                borderRadius: 6, textDecoration: "none", fontSize: 14, fontWeight: 700,
                color: active ? "#fff" : "var(--ink)",
                background: active ? "var(--burgundy)" : "transparent",
                transition: "background-color .15s ease, color .15s ease",
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--soft)"; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
            >
              <Icon size={17} />
              {label}
            </Link>
          );
        })}
      </nav>
      <div style={{ marginTop: "auto", padding: "16px 18px", borderTop: "1px solid var(--line)" }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{user?.name}</div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email}</div>
        <button onClick={doLogout} data-testid="logout-btn" className="ddp-btn ddp-btn-ghost ddp-btn-sm" style={{ width: "100%" }}>
          <LogOut size={15} /> Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--ivory)" }}>
      <div style={{ height: 3, background: "var(--burgundy)" }} data-testid="brand-accent" />
      {/* Mobile top bar */}
      <div className="flex lg:hidden" style={{ alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid var(--line)", background: "var(--paper)", position: "sticky", top: 0, zIndex: 30 }}>
        <Logo height={30} />
        <button onClick={() => setOpen(true)} className="ddp-btn ddp-btn-ghost ddp-btn-sm" data-testid="open-menu"><Menu size={18} /></button>
      </div>

      <div style={{ display: "flex" }}>
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex" style={{ width: 258, minHeight: "100vh", flexDirection: "column", borderRight: "1px solid var(--line)", background: "var(--paper)", position: "sticky", top: 0, height: "100vh" }}>
          {SideInner}
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div style={{ position: "fixed", inset: 0, zIndex: 50 }} className="lg:hidden">
            <div onClick={() => setOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(31,27,24,.45)" }} />
            <aside style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 268, display: "flex", flexDirection: "column", background: "var(--paper)", boxShadow: "0 0 40px rgba(0,0,0,.2)" }}>
              <button onClick={() => setOpen(false)} style={{ position: "absolute", right: 12, top: 16, border: 0, background: "transparent", cursor: "pointer" }}><X size={20} /></button>
              {SideInner}
            </aside>
          </div>
        )}

        <main style={{ flex: 1, minWidth: 0 }}>
          <div style={{ maxWidth: 1120, margin: "0 auto", padding: "clamp(24px,4vw,52px) clamp(18px,4vw,48px)" }}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
